import { useCallback, useEffect, useState } from 'react';
import { parseRmbAmount, type ChinaPaymentType, type Quote } from '@mypoz/shared';

import { fetchQuote } from '@/features/china-payment/api.js';

/** How long typing must pause before a quote is asked for. */
export const QUOTE_DELAY_MS = 400;

export interface QuoteState {
  /**
   * `idle`: nothing typed. `invalid`: not an amount (`parseRmbAmount`). `loading`: waiting for the
   * pause, or for the server. `ready` / `error`: the latest amount's answer.
   */
  readonly status: 'idle' | 'invalid' | 'loading' | 'ready' | 'error';
  /**
   * The newest quote for this type. While `loading` (or after an `error`) it is the previous
   * amount's, kept on screen so the figures update instead of flickering — `stale` says so.
   */
  readonly quote?: Quote;
  readonly stale: boolean;
  readonly retry: () => void;
}

/**
 * The live quote as the customer types (MYP-60). One request per pause in typing, not per key;
 * an answer that comes back after a newer request was made is dropped, so a slow reply for "50"
 * can never overwrite the quote for "500".
 */
export function useQuote(type: ChinaPaymentType, amountText: string): QuoteState {
  const rmbCents = parseRmbAmount(amountText);
  const blank = amountText.trim() === '';

  const [quote, setQuote] = useState<Quote | undefined>(undefined);
  const [status, setStatus] = useState<QuoteState['status']>('idle');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (blank) {
      setStatus('idle');
      return;
    }
    if (rmbCents === undefined) {
      setStatus('invalid');
      return;
    }

    let current = true;
    setStatus('loading');
    const timer = setTimeout(() => {
      fetchQuote(type, rmbCents).then(
        (next) => {
          if (!current) return;
          setQuote(next);
          setStatus('ready');
        },
        (cause: unknown) => {
          if (!current) return;
          console.error('[china-payment] could not get a quote', cause);
          setStatus('error');
        },
      );
    }, QUOTE_DELAY_MS);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [type, rmbCents, blank, attempt]);

  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  // A quote for another type is never shown: its fee may be in another currency entirely.
  const shown = quote?.type === type ? quote : undefined;
  const fresh = status === 'ready' && shown?.rmbCents === rmbCents;
  return {
    status,
    ...(shown && (status === 'ready' || status === 'loading' || status === 'error')
      ? { quote: shown }
      : {}),
    stale: !fresh,
    retry,
  };
}
